import type { ReactElement, ReactNode } from 'react';
import type { DocumentSection, Proposal } from '@shared/types';
import type { MomentumJourneyStage } from '../../lib/plan';
import { measureTrim, type TrimBox } from './logo-trim';
import { MOMENTUM_STAGES } from './Motifs';

/**
 * `momentum_v3` shared visual vocabulary (design.md V15, reference/
 * proposal-template-brief-v2.md §3 "Layout families" + the shared-primitives
 * paragraph under it). Every rule this file's JSX emits is styled in
 * `web/src/styles/momentum-v3.css` under `.tpl-momentum-v3` - nothing here
 * touches `.tpl-momentum` CSS or momentum_v1 components, and nothing here is
 * itself a slide: slide archetypes (CoverPage, StrategyPage, ProductDivider-
 * Page, WhyUsPage, ThankYouPage, and Phase 2/3's light archetypes) compose
 * these primitives, same as momentum_v1's archetypes compose Motifs.tsx.
 *
 * `MOMENTUM_STAGES` is reused verbatim from
 * `../momentum/Motifs` per design.md's "Web conventions" - the five-stage
 * funnel vocabulary and the visible-section page-index numbering are family-
 * agnostic facts about the document, not momentum_v1-specific presentation.
 */

/* --------------------------------------------------------------------- */
/* Frame + Footer                                                         */
/* --------------------------------------------------------------------- */

/**
 * The one frame every v2 slide mounts (brief §0.1: "identical on every
 * slide and not editable per slide"): a rounded card on the white canvas
 * (32/32/32 inset, radius 28) plus the two-zone `Footer` below it (128px,
 * flush with the card's bottom edge). `surface` is fixed by the slide
 * component that renders it, never a section property (brief §0, design.md
 * "Do not (V15)"). v3 (unlike v2) never layers a pinstripe/linework texture
 * behind the card content — no template chrome, per momentum-v3-slides.md
 * global rule 4.
 */
export function Frame({
  surface,
  proposal,
  section,
  children,
  className,
}: {
  surface: 'dark' | 'light';
  proposal: Proposal;
  section: DocumentSection;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <>
      <div className={`mv3-card mv3-card-${surface}${className ? ` ${className}` : ''}`}>
        <div className="mv3-card-content">{children}</div>
      </div>
      <Footer proposal={proposal} section={section} />
    </>
  );
}

/**
 * Zero-truncation exception (momentum-v3-slides.md global rule 2): the
 * footer name keeps a single line but must never ellipsize, so a long name
 * shrinks via `font-size` instead. Names up to `FOOTER_NAME_SOFT_LEN`
 * characters render at the normal caption size; longer names scale down
 * proportionally (floor at 60% so the text never goes illegibly small).
 */
const FOOTER_NAME_SOFT_LEN = 22;
function footerNameStyle(name: string): { fontSize: string } | undefined {
  if (name.length <= FOOTER_NAME_SOFT_LEN) return undefined;
  const scale = Math.max(0.6, FOOTER_NAME_SOFT_LEN / name.length);
  return { fontSize: `calc(var(--mv3-footer-name-size) * ${scale.toFixed(3)})` };
}

/**
 * The borderless footer: a "Prepared for" client lockup on the left
 * (`advertiser.fullLogoUrl`, else the square `advertiser.logoUrl` icon,
 * beside `advertiser.name`) and a right-justified logo strip on the right —
 * the agency (`theme.logoUrl`, else `theme.brandName` as text) rightmost, and
 * the rep's partner logos (`proposal.footerLogos`, oldest first) to its left,
 * each newer one further left (docs/footer-logo-strip.md). Client text stays
 * beside its mark because many advertiser logos are symbol-only.
 *
 * A deck with no agency identity set yet renders no agency mark — never a
 * stand-in name or logo (design.md V15 "Do not").
 */
export function Footer({ proposal }: { proposal: Proposal; section: DocumentSection }): ReactElement {
  const theme = proposal.document?.theme ?? null;
  const agencyName = theme?.brandName?.trim() || null;
  const agencyLogo = theme?.logoUrl || null;
  const clientName = proposal.advertiser.name || 'Client';
  const clientLogo = proposal.advertiser.fullLogoUrl || proposal.advertiser.logoUrl;
  const partnerLogos = [...(proposal.footerLogos ?? [])].reverse();
  const stripCount = partnerLogos.length + (agencyLogo ? 1 : 0);
  const strip = stripFit(stripCount);

  return (
    <div className="mv3-footer">
      <div className="mv3-footer-client">
        <span className="mv3-footer-label">Prepared for</span>
        {clientLogo ? <img src={clientLogo} alt="" className="mv3-footer-logo mv3-footer-logo-client" /> : null}
        <span className="mv3-footer-name" style={footerNameStyle(clientName)}>
          {clientName}
        </span>
      </div>
      <div className="mv3-footer-strip" style={{ gap: `${strip.gap}px` }}>
        {partnerLogos.map((logo) => (
          <StripLogo key={logo.id} src={logo.url} alt={logo.name} maxWidth={strip.maxWidth} />
        ))}
        {agencyLogo ? (
          <StripLogo src={agencyLogo} alt={agencyName ?? 'Agency'} maxWidth={strip.maxWidth} />
        ) : agencyName ? (
          <span className="mv3-footer-name" style={footerNameStyle(agencyName)}>
            {agencyName}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Logo-strip sizing: every mark gets the same visual AREA rather than the
 * same height, so a square icon and a long wordmark read as equals (a fixed
 * height makes wordmarks shout and icons whisper). Height and width are
 * clamped so no mark gets taller than the footer allows or wider than its
 * share of the strip. Sizing uses the logo's INK box, not its file bounds
 * (logo-trim.ts), so padding inside the file never shrinks the mark. Until
 * the image loads, CSS falls back to a fixed height. Set on the element in
 * onLoad (no state) so the footer stays hook-free.
 */
const STRIP_LOGO_AREA = 36 * 112;
const STRIP_LOGO_MIN_H = 22;
const STRIP_LOGO_MAX_H = 52;
const STRIP_LOGO_MAX_W = 176;

/**
 * The strip holds up to 9 marks (agency + FOOTER_LOGO_MAX partners). Its
 * worst case is every mark a long wordmark at full width, so the per-logo
 * width cap shrinks with the count to keep the whole strip inside
 * STRIP_BUDGET_W, leaving the rest of the footer to the client lockup. A
 * crowded strip (over 5 marks) also tightens its gaps. Up to 4 marks nothing
 * changes.
 */
const STRIP_BUDGET_W = 1000;
const STRIP_GAP = 40;
const STRIP_GAP_CROWDED = 24;

export function stripFit(count: number): { gap: number; maxWidth: number } {
  const gap = count > 5 ? STRIP_GAP_CROWDED : STRIP_GAP;
  if (count <= 1) return { gap, maxWidth: STRIP_LOGO_MAX_W };
  const maxWidth = Math.floor((STRIP_BUDGET_W - gap * (count - 1)) / count);
  return { gap, maxWidth: Math.min(STRIP_LOGO_MAX_W, maxWidth) };
}

export function stripLogoSize(
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number = STRIP_LOGO_MAX_W,
): { width: number; height: number } | null {
  if (!(naturalWidth > 0 && naturalHeight > 0)) return null;
  const ratio = naturalWidth / naturalHeight;
  let height = Math.min(STRIP_LOGO_MAX_H, Math.max(STRIP_LOGO_MIN_H, Math.sqrt(STRIP_LOGO_AREA / ratio)));
  let width = height * ratio;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / ratio;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * Where the image sits inside its strip slot once trimmed: the slot is the
 * ink box sized by stripLogoSize, and the full image is scaled and shifted
 * so only the ink shows (the slot clips the padding). `trim` null = untrimmed.
 */
export function stripLogoLayout(
  naturalWidth: number,
  naturalHeight: number,
  trim: TrimBox | null,
  maxWidth?: number,
): { slot: { width: number; height: number }; image: { width: number; height: number; left: number; top: number } } | null {
  const box = trim ?? { x: 0, y: 0, width: 1, height: 1 };
  const inkWidth = naturalWidth * box.width;
  const inkHeight = naturalHeight * box.height;
  const slot = stripLogoSize(inkWidth, inkHeight, maxWidth);
  if (!slot) return null;
  const k = slot.width / inkWidth;
  return {
    slot,
    image: {
      width: naturalWidth * k,
      height: naturalHeight * k,
      left: -box.x * naturalWidth * k,
      top: -box.y * naturalHeight * k,
    },
  };
}

function StripLogo({ src, alt, maxWidth }: { src: string; alt: string; maxWidth: number }): ReactElement {
  return (
    <span className="mv3-footer-logo-strip">
      <img
        src={src}
        alt={alt}
        onLoad={(event) => {
          const img = event.currentTarget;
          const layout = stripLogoLayout(img.naturalWidth, img.naturalHeight, measureTrim(img), maxWidth);
          const slot = img.parentElement;
          if (!layout || !slot) return;
          slot.style.width = `${layout.slot.width}px`;
          slot.style.height = `${layout.slot.height}px`;
          Object.assign(img.style, {
            position: 'absolute',
            maxWidth: 'none',
            width: `${layout.image.width}px`,
            height: `${layout.image.height}px`,
            left: `${layout.image.left}px`,
            top: `${layout.image.top}px`,
          });
        }}
      />
    </span>
  );
}

/**
 * A focal point the rep authored for a hero image, as CSS `object-position`
 * (`section.content.fields.focalPoint`, e.g. "62% 30%"). No schema change:
 * `content.fields` is already a free-form string map (src/domain/types.ts
 * `DocumentSectionContent.fields`); absent/blank falls back to the browser
 * default (`center`, via the caller simply omitting the style).
 */
export function focalPointStyle(section: DocumentSection): { objectPosition: string } | undefined {
  const focal = section.content.fields?.focalPoint?.trim();
  return focal ? { objectPosition: focal } : undefined;
}

/* --------------------------------------------------------------------- */
/* Tag / label primitives                                                */
/* --------------------------------------------------------------------- */

export type TagPillTone = 'fill' | 'outline' | 'ghost' | 'lime';

/** brief §3 "tag pill (fill / outline / ghost / lime)". Lime only on dark (brief §2). */
export function TagPill({
  tone = 'fill',
  children,
  className,
}: {
  tone?: TagPillTone;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return <span className={`mv3-tag mv3-tag-${tone}${className ? ` ${className}` : ''}`}>{children}</span>;
}

/**
 * brief §3 "stage bar (full-width pill, lime-on-dark / mint-tint-on-light)".
 * `wide` (polish pass, reference/proposal-template-wireframes-v2.html `10
 * Channel hero`: `.bar` at `width:80%`) stretches the pill to ~80% of its
 * content column instead of hugging its label - product_divider's stage bar
 * uses it; every other caller keeps the default content-width chip.
 */
export function StageBar({
  children,
  surface = 'dark',
  wide,
  className,
}: {
  children: ReactNode;
  surface?: 'dark' | 'light';
  wide?: boolean;
  className?: string;
}): ReactElement {
  return (
    <div
      className={`mv3-stagebar mv3-stagebar-${surface}${wide ? ' mv3-stagebar-wide' : ''}${className ? ` ${className}` : ''}`}
    >
      {children}
    </div>
  );
}

/** brief §3 "numbered label (`01` in mint, not a ring)". */
export function NumberedLabel({
  number,
  label,
  inverse,
  className,
}: {
  number: string;
  label: string;
  /** On a dark ground - lightens the label tone, same convention as momentum_v1's Eyebrow. */
  inverse?: boolean;
  className?: string;
}): ReactElement {
  return (
    <div className={`mv3-numbered-label${inverse ? ' mv3-numbered-label-inverse' : ''}${className ? ` ${className}` : ''}`}>
      <span className="mv3-numbered-label-num">{number}</span>
      <span className="mv3-numbered-label-sep" aria-hidden="true">
        /
      </span>
      <span className="mv3-numbered-label-text">{label}</span>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Content primitives                                                    */
/* --------------------------------------------------------------------- */

/** brief §3 "hairline row list" - mint bullet + one line each (why-us proof points, etc). */
export function HairlineList({ items, className }: { items: ReactNode[]; className?: string }): ReactElement {
  return (
    <ul className={`mv3-hairline-list${className ? ` ${className}` : ''}`}>
      {items.map((item, index) => (
        <li className="mv3-hairline-row" key={index}>
          <span className="mv3-hairline-dot" aria-hidden="true" />
          <span className="mv3-hairline-text">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** brief §2 "sub-cards on light: --bg-2 with 1px hairline border ... dark panel on light". */
export function SubCard({
  tone = 'light',
  className,
  children,
}: {
  tone?: 'light' | 'dark';
  className?: string;
  children: ReactNode;
}): ReactElement {
  return <div className={`mv3-subcard mv3-subcard-${tone}${className ? ` ${className}` : ''}`}>{children}</div>;
}

/** brief §3 "stat tile" - renders nothing when the value is absent (never fabricated). */
export function StatTile({
  label,
  value,
  note,
  tone,
  className,
}: {
  label: string;
  value: ReactNode | null | undefined;
  note?: ReactNode;
  tone?: 'light' | 'dark';
  className?: string;
}): ReactElement | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className={`mv3-stat-tile${tone ? ` mv3-stat-tile-${tone}` : ''}${className ? ` ${className}` : ''}`}>
      <span className="mv3-stat-tile-label">{label}</span>
      <span className="mv3-stat-tile-value">{value}</span>
      {note ? <span className="mv3-stat-tile-note">{note}</span> : null}
    </div>
  );
}

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** CSS `grid-template-columns` track, e.g. "2fr" or "140px". Default "1fr". */
  width?: string;
}

/** brief §3 "table (header row, body rows, total row with mint-tint)". */
export function Table({
  columns,
  rows,
  totalRow,
  className,
}: {
  columns: TableColumn[];
  rows: Record<string, ReactNode>[];
  /** Rendered with the mint-tint background (brief §2); omit for no total row. */
  totalRow?: Record<string, ReactNode>;
  className?: string;
}): ReactElement {
  const gridTemplateColumns = columns.map((col) => col.width ?? '1fr').join(' ');
  const cell = (col: TableColumn, value: ReactNode, key: string) => (
    <span key={key} className={col.align === 'right' ? 'mv3-table-cell-right' : undefined}>
      {value}
    </span>
  );
  const row = (content: ReactNode, key: string | number, extraClass?: string) => (
    <div className={`mv3-table-row${extraClass ? ` ${extraClass}` : ''}`} key={key} style={{ gridTemplateColumns }}>
      {content}
    </div>
  );
  return (
    <div className={`mv3-table${className ? ` ${className}` : ''}`}>
      {row(columns.map((col) => cell(col, col.label, col.key)), 'head', 'mv3-table-head')}
      {rows.map((data, index) => row(columns.map((col) => cell(col, data[col.key], col.key)), index))}
      {totalRow ? row(columns.map((col) => cell(col, totalRow[col.key], col.key)), 'total', 'mv3-table-total') : null}
    </div>
  );
}

/** brief §3 "funnel strip (4 segments...)"; design.md V15 decision 6: v2 always uses the FIVE-segment `MOMENTUM_STAGES` vocabulary, not four. */
export function FunnelStrip({
  activeStage,
  className,
}: {
  activeStage?: MomentumJourneyStage | null;
  className?: string;
}): ReactElement {
  return (
    <div className={`mv3-funnel-strip${className ? ` ${className}` : ''}`}>
      {MOMENTUM_STAGES.map((stage) => (
        <div
          key={stage.key}
          className={`mv3-funnel-seg${stage.key === activeStage ? ' mv3-funnel-seg-active' : ''}`}
        >
          {stage.label}
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Charts                                                                 */
/* --------------------------------------------------------------------- */

/** First slice mint, then stepped greys/darks (brief §4 `mediaMix`). */
const DEFAULT_DONUT_COLORS = ['var(--mv3-mint)', '#3A3F44', '#6B6F6A', '#9AA0A6', '#C9CBC4', '#DCDCD6'];

export interface DonutSlice {
  value: number;
  color?: string;
}

/** brief §3 "donut" - inline SVG (never a CSS conic-gradient) so print/export renders identically. */
export function Donut({
  slices,
  size = 160,
  strokeWidth = 28,
  centerValue,
  centerLabel,
  className,
}: {
  slices: DonutSlice[];
  size?: number;
  strokeWidth?: number;
  centerValue?: ReactNode;
  centerLabel?: ReactNode;
  className?: string;
}): ReactElement {
  const total = slices.reduce((sum, slice) => sum + Math.max(slice.value, 0), 0) || 1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className={`mv3-donut${className ? ` ${className}` : ''}`} style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {slices.map((slice, index) => {
            const fraction = Math.max(slice.value, 0) / total;
            const length = fraction * circumference;
            const dashOffset = -offset;
            offset += length;
            return (
              <circle
                key={index}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={slice.color ?? DEFAULT_DONUT_COLORS[index % DEFAULT_DONUT_COLORS.length]}
                strokeWidth={strokeWidth}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={dashOffset}
              />
            );
          })}
        </g>
      </svg>
      {centerValue || centerLabel ? (
        <div className="mv3-donut-center">
          {centerValue ? <span className="mv3-donut-value">{centerValue}</span> : null}
          {centerLabel ? <span className="mv3-donut-label">{centerLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export interface BarChartSeries {
  label: string;
  values: number[];
  color?: string;
}

/** brief §3 "bar chart" - inline SVG, two series (conservative/target on `projections`). */
export function BarChart({
  seriesA,
  seriesB,
  height = 160,
  width = 480,
  className,
}: {
  seriesA: BarChartSeries;
  seriesB?: BarChartSeries;
  height?: number;
  width?: number;
  className?: string;
}): ReactElement {
  const max = Math.max(...seriesA.values, ...(seriesB?.values ?? []), 1);
  const count = Math.max(seriesA.values.length, seriesB?.values.length ?? 0, 1);
  const groupWidth = width / count;
  const gap = groupWidth * 0.12;
  const barWidth = seriesB ? (groupWidth - gap * 3) / 2 : groupWidth - gap * 2;
  const bars = (series: BarChartSeries | undefined, slot: 0 | 1, fallbackColor: string) =>
    series
      ? series.values.map((value, index) => {
          const barHeight = (Math.max(value, 0) / max) * height;
          const x = index * groupWidth + gap + slot * (barWidth + gap);
          return (
            <rect
              key={`${slot}-${index}`}
              x={x}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
              rx={3}
              fill={series.color ?? fallbackColor}
            />
          );
        })
      : null;
  return (
    <svg
      className={`mv3-barchart${className ? ` ${className}` : ''}`}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-hidden="true"
    >
      {/* Both current callers (monthly_schedule, projections) render this on
          a light Frame - `--mv3-green-deep`, not raw mint, per the global
          light-surface colour rule. */}
      {bars(seriesA, 0, 'var(--mv3-green-deep)')}
      {bars(seriesB, 1, '#C9CBC4')}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* Layout primitives                                                     */
/* --------------------------------------------------------------------- */

/** brief §3 "headrow (h1 left + right-aligned tagline)". */
export function HeadRow({
  title,
  tagline,
  className,
}: {
  title: ReactNode;
  tagline?: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div className={`mv3-headrow${className ? ` ${className}` : ''}`}>
      <h1 className="mv3-h1">{title}</h1>
      {tagline ? <p className="mv3-headrow-tagline">{tagline}</p> : null}
    </div>
  );
}

/** brief §3 "signature line" - one signatory cell (terms, brief §4 `18 terms`). */
export function SignatureLine({ label, className }: { label: string; className?: string }): ReactElement {
  return (
    <div className={`mv3-signature-line${className ? ` ${className}` : ''}`}>
      <span>{label}</span>
    </div>
  );
}
